import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { TypeIcon, HeadingIcon, MousePointerIcon, ImageIcon, PlayIcon, MinusIcon, MoveVerticalIcon, ColumnsIcon, Share2Icon, UserMinusIcon, LayoutIcon, SquareIcon, TableIcon, CodeIcon, FootprintsIcon, UndoIcon, RedoIcon, PaletteIcon, ChevronRightIcon, TagIcon, XIcon, WandSparklesIcon, Loader2Icon, SendIcon, PlusIcon, ChevronDownIcon, SettingsIcon, CopyIcon, Trash2Icon, GripVerticalIcon, SearchIcon, ImagePlusIcon, SlidersHorizontalIcon, SparklesIcon } from 'lucide-react';
import { EmailBlock, EmailBlockType, EmailGlobalStyles, DEFAULT_EMAIL_STYLES, EMAIL_PALETTE, createEmailBlock, generateId } from './types';
import { generateEmailHtml } from './emailHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
import { mediaLibraryApi, MediaLibraryRecord } from '../../services/creatorApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Heading: HeadingIcon,
  MousePointer: MousePointerIcon,
  Image: ImageIcon,
  Play: PlayIcon,
  Minus: MinusIcon,
  MoveVertical: MoveVerticalIcon,
  Columns: ColumnsIcon,
  Share2: Share2Icon,
  UserMinus: UserMinusIcon,
  Layout: LayoutIcon,
  Square: SquareIcon,
  Table: TableIcon,
  Code: CodeIcon,
  Footprints: FootprintsIcon
};
const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok', 'pinterest', 'whatsapp'];
const EMAIL_BEAUTIFY_INSTRUCTIONS = `You are an expert email HTML developer and designer. You receive an email's complete HTML. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY an HTML code block with the complete email HTML.
2. Keep ALL merge variables like {{confirmation_link}}, {{team_name}}, {{first_name}} etc.
3. Keep the TABLE-BASED layout for email client compatibility.
4. Use inline styles (email clients strip style blocks).
5. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles, content, and structure exactly as they are.
6. If the user says "make the button green", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, or anything else.
7. If the user says "change the heading font size", ONLY change that. Do NOT redesign the entire email.
8. Start with the EXACT current HTML and make MINIMAL targeted changes based on the user's instruction.
9. Keep it responsive with the existing @media queries.
10. Do NOT add any explanation — ONLY the html code block:
\`\`\`html
(your modified email HTML here)
\`\`\`
11. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseHtmlFromResponse(response: string): string {
  const htmlMatch = response.match(/```html\s*\n([\s\S]*?)```/i);
  if (htmlMatch) return htmlMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
// Types that can be placed inside columns
const COLUMN_ALLOWED_TYPES: EmailBlockType[] = ['text', 'heading', 'button', 'image', 'divider', 'spacer', 'boxed_text', 'html_block', 'image_text', 'table'];
interface EmailBuilderProps {
  initialBlocks?: EmailBlock[];
  initialStyles?: EmailGlobalStyles;
  onSchemaChange: (blocks: EmailBlock[], styles: EmailGlobalStyles) => void;
  onHtmlChange: (html: string) => void;
  mergeVariables?: string[];
}
export function EmailBuilder({
  initialBlocks,
  initialStyles,
  onSchemaChange,
  onHtmlChange,
  mergeVariables = []
}: EmailBuilderProps) {
  const [blocks, setBlocks] = useState<EmailBlock[]>(initialBlocks || []);
  const [styles, setStyles] = useState<EmailGlobalStyles>(initialStyles || DEFAULT_EMAIL_STYLES);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Track selected child block inside a column: { parentId, colIndex, childId }
  const [selectedChild, setSelectedChild] = useState<{
    parentId: string;
    colIndex: number;
    childId: string;
  } | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  // Track drag over column: { parentId, colIndex, position }
  const [dragOverCol, setDragOverCol] = useState<{
    parentId: string;
    colIndex: number;
    position: number;
  } | null>(null);
  const [history, setHistory] = useState<EmailBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'elements' | 'layouts' | 'design'>('elements');
  // Track HTML before beautify so undo can revert it
  const [preBeautifyHtml, setPreBeautifyHtml] = useState<string | null>(null);
  // Column configuration modal state
  const [columnConfigModal, setColumnConfigModal] = useState<{
    isOpen: boolean;
    blockId: string;
    currentRatio: string;
  } | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: EmailBlockType;
    index?: number;
  } | null>(null);
  // Find the selected block — could be top-level or a child inside a column
  const selectedBlock = (() => {
    if (selectedChild) {
      const parent = blocks.find(b => b.id === selectedChild.parentId);
      if (parent?.columns?.[selectedChild.colIndex]) {
        return parent.columns[selectedChild.colIndex].find(cb => cb.id === selectedChild.childId) || null;
      }
      return null;
    }
    return blocks.find(b => b.id === selectedId) || null;
  })();
  const pushHistory = useCallback((newBlocks: EmailBlock[]) => {
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newBlocks].slice(-50));
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: EmailBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const html = generateEmailHtml(newBlocks, styles);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(html);
  }, [styles, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    // Only sync generated HTML when the builder has blocks.
    // This prevents empty builder initialization from clearing the code editor.
    if (blocks.length > 0) {
      const html = generateEmailHtml(blocks, styles);
      onSchemaChange(blocks, styles);
      onHtmlChange(html);
    }
  }, [styles]);
  const undo = useCallback(() => {
    // If we have a beautify HTML to revert, do that first
    if (preBeautifyHtml !== null) {
      onHtmlChange(preBeautifyHtml);
      setPreBeautifyHtml(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      onSchemaChange(prev, styles);
      onHtmlChange(generateEmailHtml(prev, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange, preBeautifyHtml]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      onSchemaChange(next, styles);
      onHtmlChange(generateEmailHtml(next, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  const handlePaletteDragStart = (type: EmailBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', type); // needed for column drops
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(index);
    setDragOverCol(null);
  };
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
    setDragOverCol(null);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      newBlocks.splice(drag.index < dropIndex ? dropIndex - 1 : dropIndex, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (drag?.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentRatio: newBlock.columnRatio || '50-50'
        });
      }
    }
    dragItemRef.current = null;
  };
  // Column drop handlers
  const handleColumnDragOver = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = dragItemRef.current;
    if (!drag) return;
    // Only allow palette drops of allowed types into columns
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const parent = blocks.find(b => b.id === parentId);
      const col = parent?.columns?.[colIndex] || [];
      setDragOverCol({
        parentId,
        colIndex,
        position: col.length
      });
      setDragOverIdx(null);
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  const handleColumnDrop = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, newBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedChild({
        parentId,
        colIndex,
        childId: newBlock.id
      });
      setSelectedId(null);
    }
    dragItemRef.current = null;
  };
  const deleteBlock = (id: string) => {
    updateBlocks(blocks.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const deleteChildBlock = (parentId: string, colIndex: number, childId: string) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.filter(cb => cb.id !== childId) : col);
      return {
        ...b,
        columns: cols
      };
    });
    updateBlocks(newBlocks);
    if (selectedChild?.childId === childId) setSelectedChild(null);
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const original = blocks[idx];
    const clone: EmailBlock = {
      ...original,
      id: generateId(),
      columns: original.columns ? original.columns.map(col => col.map(cb => ({
        ...cb,
        id: generateId()
      }))) : undefined
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
    setSelectedChild(null);
  };
  const updateBlock = (id: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => b.id === id ? {
      ...b,
      ...updates
    } : b);
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const updateChildBlock = (parentId: string, colIndex: number, childId: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.map(cb => cb.id === childId ? {
        ...cb,
        ...updates
      } : cb) : col);
      return {
        ...b,
        columns: cols
      };
    });
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const commitToHistory = () => pushHistory(blocks);
  const categories = [...new Set(EMAIL_PALETTE.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const html = generateEmailHtml(blocks, styles);
      // Save current HTML so undo can revert
      setPreBeautifyHtml(html);
      const prompt = `Here is my email HTML:\n\`\`\`html\n${html}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the email. Keep the overall look intact.'}`;
      const result = await callGPTAPI(EMAIL_BEAUTIFY_INSTRUCTIONS, prompt);
      const newHtml = parseHtmlFromResponse(result);
      if (newHtml) onHtmlChange(newHtml);
    } catch (err) {
      console.error('Email beautify failed:', err);
      setPreBeautifyHtml(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Handle property changes — route to correct updater based on selection
  const handlePropertyChange = (updates: Partial<EmailBlock>) => {
    if (selectedChild) {
      updateChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId, updates);
    } else if (selectedId) {
      updateBlock(selectedId, updates);
    }
  };
  const handlePropertyDelete = () => {
    if (selectedChild) {
      deleteChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId);
    } else if (selectedId) {
      deleteBlock(selectedId);
    }
  };
  const insertLayout = (layoutType: string) => {
    const newBlocks = [...blocks];
    switch (layoutType) {
      case 'image-text':
        newBlocks.push(createEmailBlock('image'), createEmailBlock('text'));
        break;
      case 'text-image-side':
        const cols = createEmailBlock('columns');
        cols.columns = [[createEmailBlock('text')], [createEmailBlock('image')]];
        newBlocks.push(cols);
        break;
      case 'image-text-side':
        const cols2 = createEmailBlock('columns');
        cols2.columns = [[createEmailBlock('image')], [createEmailBlock('text')]];
        newBlocks.push(cols2);
        break;
      case 'three-col':
        const cols3 = createEmailBlock('columns');
        cols3.columnRatio = '33-33-33';
        cols3.columns = [[createEmailBlock('image'), createEmailBlock('text')], [createEmailBlock('image'), createEmailBlock('text')], [createEmailBlock('image'), createEmailBlock('text')]];
        newBlocks.push(cols3);
        break;
      case 'hero':
        newBlocks.push(createEmailBlock('image'), createEmailBlock('heading'), createEmailBlock('text'), createEmailBlock('button'));
        break;
    }
    updateBlocks(newBlocks);
  };
  return <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-138" data-mp="3oglx0bclgq6k">
      {/* Left — Sidebar */}
      <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col" data-id="element-139" data-mp="1azkbouba42ut">
        <div className="flex border-b border-gray-200 bg-white" data-id="element-140" data-mp="1fxcbeai60t9k">
          <button onClick={() => setSidebarTab('elements')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'elements' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-141" data-mp="1jy4ml9all88h">
            Elements
          </button>
          <button onClick={() => setSidebarTab('layouts')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'layouts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-142" data-mp="1ytkaewkl47wf">
            Layouts
          </button>
          <button onClick={() => setSidebarTab('design')} className={`flex-1 py-2.5 text-xs font-medium text-center border-b-2 transition-colors ${sidebarTab === 'design' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-143" data-mp="2hlmu6gsiw4aw">
            Design
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-144" data-mp="2fnjanlkagqpo">
          {sidebarTab === 'elements' && <>
              {categories.map(cat => <div key={cat} data-id="element-145" data-mp="2pntptbpvw87h">
                  <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-146" data-mp="2pmijb4520mco">
                    {cat}
                    <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-147" data-mp="33d3vka005y1l" />
                  </button>
                  {!collapsedCats.has(cat) && <div className="px-2 pb-2 grid grid-cols-2 gap-1" data-id="element-148" data-mp="11p9eod8ndm6p">
                      {EMAIL_PALETTE.filter(p => p.category === cat).map(item => {
                const Icon = ICONS[item.icon] || TypeIcon;
                return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as EmailBlockType)} className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-md bg-white border border-gray-200 text-xs text-gray-600 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none text-center" data-id="element-149" data-mp="rswkzfd3r8by">
                              <Icon className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-150" data-mp="237tbktoxq5lf" />
                              <span className="truncate w-full" data-id="element-151" data-mp="1yeqggh4ieo34">
                                {item.label}
                              </span>
                            </div>;
              })}
                    </div>}
                </div>)}

              {mergeVariables.length > 0 && <div className="border-t border-gray-200 mt-2 pt-3 px-3 pb-4 bg-blue-50/50" data-id="element-152" data-mp="3nvvre15y9d12">
                  <div className="flex items-center gap-1.5 mb-1.5" data-id="element-153" data-mp="39wmo222uedei">
                    <TagIcon className="h-3.5 w-3.5 text-blue-500" data-id="element-154" data-mp="3dyhnf117qgpn" />
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-155" data-mp="3dzsqszqqxmg8">
                      Merge Tags
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mb-2.5 leading-relaxed" data-id="element-156" data-mp="34ykugn7sivos">
                    Click to copy. Paste these into text blocks to personalize
                    emails for each recipient.
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-157" data-mp="134c1d1js1dhh">
                    {mergeVariables.map(v => <button key={v} onClick={() => {
                navigator.clipboard?.writeText(`{{${v}}}`);
                // Optional: could add a tiny toast here, but native browser copy is usually enough
              }} className="text-[10px] px-2 py-1 bg-white text-blue-700 border border-blue-200 rounded shadow-sm hover:bg-blue-50 hover:border-blue-300 transition-colors font-mono" title={`Copy {{${v}}}`} data-id="element-158" data-mp="2ipe2quo1d1ig">
                        {`{{${v}}}`}
                      </button>)}
                  </div>
                </div>}
            </>}

          {sidebarTab === 'layouts' && <div className="p-3 space-y-3" data-id="element-159" data-mp="1m17u7wnztoyw">
              <p className="text-xs text-gray-500 mb-2" data-id="element-160" data-mp="1h0sh967mijhg">
                Click to insert layout at the bottom
              </p>

              <button onClick={() => insertLayout('image-text')} className="w-full flex flex-col gap-1 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-161" data-mp="82k7ngyun5tl">
                <div className="h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-162" data-mp="hz5dg4ip0kyh">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-163" data-mp="m21zy6bct17p" />
                </div>
                <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-164" data-mp="3ljjks4pyoopg"></div>
                <div className="h-2 bg-gray-200 rounded w-full" data-id="element-165" data-mp="3qga18t5ohmbe"></div>
                <div className="h-2 bg-gray-200 rounded w-5/6" data-id="element-166" data-mp="3uioix9xm2g13"></div>
              </button>

              <button onClick={() => insertLayout('text-image-side')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-167" data-mp="33z7fhjlkm55">
                <div className="flex-1 flex flex-col gap-1 justify-center" data-id="element-168" data-mp="36su6uul48vhw">
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-169" data-mp="36qptlm1buk4y"></div>
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-170" data-mp="2z91f5jj1s6ea"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-171" data-mp="2ubje0fb9uvgu"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-172" data-mp="2q83khrui1b8d">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-173" data-mp="2lal8fsl6qfw5" />
                </div>
              </button>

              <button onClick={() => insertLayout('image-text-side')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-174" data-mp="2gbqqesv1kvd3">
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center" data-id="element-175" data-mp="26loz7s7e6wty">
                  <ImageIcon className="h-4 w-4 text-gray-400" data-id="element-176" data-mp="22klk57x654ts" />
                </div>
                <div className="flex-1 flex flex-col gap-1 justify-center" data-id="element-177" data-mp="1xj62lpu79gg5">
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-178" data-mp="1skl0fkve0mp2"></div>
                  <div className="h-2 bg-gray-200 rounded w-full" data-id="element-179" data-mp="1sngz5bf27fek"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4" data-id="element-180" data-mp="1ivxnidxrsqyw"></div>
                </div>
              </button>

              <button onClick={() => insertLayout('three-col')} className="w-full flex gap-2 p-2 bg-white border border-gray-200 rounded hover:border-blue-400" data-id="element-181" data-mp="1nuincc9418mt">
                {[1, 2, 3].map(i => <div key={i} className="flex-1 flex flex-col gap-1" data-id="element-182" data-mp="21nqek2ta423z">
                    <div className="h-8 bg-gray-200 rounded flex items-center justify-center" data-id="element-183" data-mp="26n38f7iki9g2">
                      <ImageIcon className="h-3 w-3 text-gray-400" data-id="element-184" data-mp="1ry6tcqi3fw5y" />
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded w-full" data-id="element-185" data-mp="1wxa0cghmwcgk"></div>
                    <div className="h-1.5 bg-gray-200 rounded w-4/5" data-id="element-186" data-mp="1wvgp0nw2t428"></div>
                  </div>)}
              </button>

              <button onClick={() => insertLayout('hero')} className="w-full flex flex-col gap-1.5 p-2 bg-white border border-gray-200 rounded hover:border-blue-400 items-center" data-id="element-187" data-mp="2pdb10von5hi0">
                <div className="h-16 w-full bg-gray-200 rounded flex items-center justify-center" data-id="element-188" data-mp="8yhffyzkn18t">
                  <ImageIcon className="h-5 w-5 text-gray-400" data-id="element-189" data-mp="nndl5pe5o269" />
                </div>
                <div className="h-3 bg-gray-300 rounded w-1/2 mt-1" data-id="element-190" data-mp="ip29d94avhle"></div>
                <div className="h-1.5 bg-gray-200 rounded w-3/4" data-id="element-191" data-mp="3r4vp3lqt4p1e"></div>
                <div className="h-1.5 bg-gray-200 rounded w-2/3" data-id="element-192" data-mp="3r7853td08pbt"></div>
                <div className="h-4 bg-blue-400 rounded w-1/3 mt-1" data-id="element-193" data-mp="3m7cagkq76a8x"></div>
              </button>
            </div>}

          {sidebarTab === 'design' && <EmailStyleProperties styles={styles} onChange={setStyles} data-id="element-194" data-mp="3w3p4xktkgs4q" />}
        </div>
      </div>

      {/* Center — Canvas */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => {
      setSelectedId(null);
      setSelectedChild(null);
    }} data-id="element-195" data-mp="1fe02yo0bd9gz">
        <div className="flex items-center justify-between mb-4" data-id="element-196" data-mp="34xrm89wgyhy7">
          <div className="flex items-center gap-1" data-id="element-197" data-mp="39vt790dggrcb">
            <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo" data-id="element-198" data-mp="39wb8kbez5vr3">
              <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-199" data-mp="3jmxibp00fs1v" />
            </button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo" data-id="element-200" data-mp="3nmeqwraqh5b9">
              <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-201" data-mp="1dwyrvyevcnw" />
            </button>
          </div>

          <div className="flex items-center gap-2" data-id="element-202" data-mp="6eb6te00imlx">
            {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-203" data-mp="2397codt8svq4">
                <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleBeautify(beautifyPrompt);
              if (e.key === 'Escape') {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }
            }} placeholder="e.g. warm colors, minimal, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-204" data-mp="288ahbx5b8zxk" />
                <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-205" data-mp="zbltuf4virje">
                  <SendIcon className="h-3.5 w-3.5" data-id="element-206" data-mp="3f03ykmfyp229" />
                </button>
                <button onClick={() => {
              setShowBeautifyInput(false);
              setBeautifyPrompt('');
            }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-207" data-mp="2d90n8k5g5yut">
                  <XIcon className="h-3.5 w-3.5" data-id="element-208" data-mp="2m2vpnkf6pb6r" />
                </button>
              </div>}
            <button onClick={() => {
            if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
          }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your email" data-id="element-209" data-mp="2h9b1ek3e7vkr">
              {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-210" data-mp="16m52a8wa1u2e" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-211" data-mp="whor3stqt14x" />}
              {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
            </button>
            <span className="text-xs text-gray-400" data-id="element-212" data-mp="mr2dg45l6kov">
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="mx-auto rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{
        maxWidth: styles.contentWidth,
        backgroundColor: styles.innerColor,
        border: styles.outerBorderSize > 0 ? `${styles.outerBorderSize}px solid ${styles.outerBorderColor}` : 'none'
      }} dir={styles.rtlEnabled ? 'rtl' : 'ltr'} data-id="element-213" data-mp="rnstwslaziat">
          <div style={{
          backgroundColor: styles.innerColor
        }} data-id="element-214" data-mp="1y5pmmjafwj49">
            {blocks.length === 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(0);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg p-16 text-center transition-colors m-4 ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-215" data-mp="232pxd7i8ia1p">
                <p className="text-sm text-gray-400" data-id="element-216" data-mp="2keo3sqljnk2k">Drag email blocks here</p>
              </div>}

            {blocks.map((block, idx) => <Fragment key={block.id} data-id="element-217" data-mp="2fhnwydgvedae" data-mpi={idx}>
                <div onDragOver={handleDragOver(idx)} onDragLeave={() => {
              setDragOverIdx(null);
              setDragOverCol(null);
            }} onDrop={handleDrop(idx)} className={`transition-all ${dragOverIdx === idx ? 'h-2 bg-blue-400' : 'h-0.5'}`} data-id="element-218" data-mp="2antetegwy6df" />
                <div draggable onDragStart={handleCanvasDragStart(idx)} onDragOver={handleBlockDragOver(idx)} onDrop={handleBlockDrop(idx)} onClick={e => {
              e.stopPropagation();
              setSelectedId(block.id);
              setSelectedChild(null);
            }} className={`group relative border-2 transition-colors ${selectedId === block.id && !selectedChild ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}`} data-id="element-219" data-mp="35pk9gjnzsrae">
                  <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-220" data-mp="28vd1qi4ixyg">
                    {block.type === 'columns' && <button onClick={e => {
                  e.stopPropagation();
                  setColumnConfigModal({
                    isOpen: true,
                    blockId: block.id,
                    currentRatio: block.columnRatio || '50-50'
                  });
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" title="Configure Columns" data-id="element-221" data-mp="79j1q5ivr2pi">
                        <SettingsIcon className="h-3 w-3 text-gray-500" data-id="element-222" data-mp="1iohpe91r4azh" />
                      </button>}
                    <button onClick={e => {
                  e.stopPropagation();
                  duplicateBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" data-id="element-223" data-mp="v0hpaiijv888">
                      <CopyIcon className="h-3 w-3 text-gray-500" data-id="element-224" data-mp="2fe8gdyau3g0j" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  deleteBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" data-id="element-225" data-mp="2af5o7efyd49z">
                      <Trash2Icon className="h-3 w-3 text-red-500" data-id="element-226" data-mp="28y8aqv6veqn7" />
                    </button>
                  </div>
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-227" data-mp="2dw9uzjg18w09">
                    <GripVerticalIcon className="h-4 w-4 text-gray-400 cursor-grab" data-id="element-228" data-mp="31n5smx766lff" />
                  </div>

                  {/* Special rendering for columns with drop zones */}
                  {block.type === 'columns' ? <EmailColumnsCanvas block={block} styles={styles} selectedChild={selectedChild} dragOverCol={dragOverCol} onColumnDragOver={handleColumnDragOver} onColumnDrop={handleColumnDrop} onSelectChild={(colIndex, childId) => {
                setSelectedChild({
                  parentId: block.id,
                  colIndex,
                  childId
                });
                setSelectedId(null);
              }} onDeleteChild={(colIndex, childId) => deleteChildBlock(block.id, colIndex, childId)} data-id="element-229" data-mp="2rv009md0a1zp" /> : <EmailCanvasBlock block={block} styles={styles} data-id="element-230" data-mp="3bhl3rnfyg6ln" />}
                </div>
              </Fragment>)}

            {blocks.length > 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(blocks.length);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleDrop(blocks.length)} className={`transition-all ${dragOverIdx === blocks.length ? 'h-2 bg-blue-400' : 'h-4'}`} data-id="element-231" data-mp="31l82a3e64ogc" />}
          </div>
        </div>
      </div>

      {/* Right — Properties */}
      <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto" data-id="element-232" data-mp="1h2nryjvw6l7d">
        {selectedBlock ? <EmailBlockProperties block={selectedBlock} isChild={!!selectedChild} onChange={handlePropertyChange} onBlur={commitToHistory} onDelete={handlePropertyDelete} data-id="element-233" data-mp="1m1qyk8riszhg" /> : <div className="p-6 text-center text-gray-400 text-sm mt-20" data-id="element-234" data-mp="tagzhvtaeyd8">
            <MousePointerIcon className="h-8 w-8 mx-auto mb-3 opacity-20" data-id="element-235" data-mp="yad7e653v9y2" />
            Select a block to edit its properties
          </div>}
      </div>

      {/* Column Configuration Modal */}
      {columnConfigModal && columnConfigModal.isOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-236" data-mp="alk57rxw45v0">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-237" data-mp="ajz9w03r4iw3">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setColumnConfigModal(null)} data-id="element-238" data-mp="304nqjx1qq2wd" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-239" data-mp="3964zmvgqd4bk">
              <div className="flex items-center justify-between mb-4" data-id="element-240" data-mp="3538nnnlp46gl">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-241" data-mp="3j3003jwkdesh">
                  Configure Columns
                </h3>
                <button onClick={() => setColumnConfigModal(null)} className="text-gray-400 hover:text-gray-500" data-id="element-242" data-mp="3e4ygmxv87dgh">
                  <XIcon className="h-5 w-5" data-id="element-243" data-mp="3ntzuoii7nkrt" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-244" data-mp="3nwcdexvslz5x">
                <div data-id="element-245" data-mp="5pes2c5i2j7r">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-246" data-mp="5owlall7omll">
                    Column Layout
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-247" data-mp="kb2answ21foi">
                    {[{
                  value: '50-50',
                  label: '50 / 50',
                  cols: 2
                }, {
                  value: '33-67',
                  label: '33 / 67',
                  cols: 2
                }, {
                  value: '67-33',
                  label: '67 / 33',
                  cols: 2
                }, {
                  value: '33-33-33',
                  label: '33 / 33 / 33',
                  cols: 3
                }].map(option => <button key={option.value} onClick={() => {
                  const block = blocks.find(b => b.id === columnConfigModal.blockId);
                  if (block) {
                    const cols = [...(block.columns || [[], []])];
                    while (cols.length < option.cols) cols.push([]);
                    updateBlock(block.id, {
                      columnRatio: option.value as any,
                      columns: cols.slice(0, option.cols)
                    });
                  }
                  setColumnConfigModal(null);
                }} className={`py-2 px-3 border rounded-md text-sm font-medium transition-colors ${columnConfigModal.currentRatio === option.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-248" data-mp="3soj63lpc00oa">
                        {option.label}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// ── Columns Canvas with Drop Zones ──
function EmailColumnsCanvas({
  block,
  styles,
  selectedChild,
  dragOverCol,
  onColumnDragOver,
  onColumnDrop,
  onSelectChild,
  onDeleteChild
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
  selectedChild: {
    parentId: string;
    colIndex: number;
    childId: string;
  } | null;
  dragOverCol: {
    parentId: string;
    colIndex: number;
    position: number;
  } | null;
  onColumnDragOver: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onColumnDrop: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onSelectChild: (colIndex: number, childId: string) => void;
  onDeleteChild: (colIndex: number, childId: string) => void;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 10,
    paddingBottom: block.paddingBottom ?? 10,
    paddingLeft: block.paddingLeft ?? 20,
    paddingRight: block.paddingRight ?? 20
  };
  const bg = block.backgroundColor || undefined;
  const cols = block.columns || [[], []];
  const isOver = (colIdx: number) => dragOverCol?.parentId === block.id && dragOverCol?.colIndex === colIdx;
  return <div style={{
    ...pad,
    backgroundColor: bg
  }} data-id="element-249" data-mp="2xrv1r92hte0y">
      <div style={{
      display: 'flex',
      gap: 0
    }} data-id="element-250" data-mp="32sit5vumvwvp">
        {cols.map((col, colIdx) => <div key={colIdx} onDragOver={onColumnDragOver(block.id, colIdx)} onDragLeave={e => e.stopPropagation()} onDrop={onColumnDrop(block.id, colIdx)} onClick={e => e.stopPropagation()} style={{
        flex: 1,
        border: isOver(colIdx) ? '2px dashed #3b82f6' : '1px dashed #d1d5db',
        backgroundColor: isOver(colIdx) ? '#eff6ff' : undefined,
        minHeight: 60,
        padding: 4,
        transition: 'border-color 0.15s, background-color 0.15s'
      }} data-id="element-251" data-mp="36st4lo173fhy" data-mpi={colIdx}>
            {col.length === 0 ? <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 12,
          padding: '16px 8px'
        }} data-id="element-252" data-mp="1etyqllrgqj6j">
                {isOver(colIdx) ? '⬇ Drop here' : `Column ${colIdx + 1} — drag blocks here`}
              </p> : col.map(cb => <div key={cb.id} onClick={e => {
          e.stopPropagation();
          onSelectChild(colIdx, cb.id);
        }} className="relative group/child" style={{
          border: selectedChild?.parentId === block.id && selectedChild?.childId === cb.id ? '2px solid #3b82f6' : '1px solid transparent',
          borderRadius: 4,
          marginBottom: 2,
          cursor: 'pointer'
        }} data-id="element-253" data-mp="1xm144o8t5jck">
                  <div className="absolute -right-0.5 -top-0.5 opacity-0 group-hover/child:opacity-100 transition-opacity z-10" data-id="element-254" data-mp="1r46bas9dz9ln">
                    <button onClick={e => {
              e.stopPropagation();
              onDeleteChild(colIdx, cb.id);
            }} className="p-0.5 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" title="Remove from column" data-id="element-255" data-mp="1w2zpyo0et6ne">
                      <XIcon className="h-2.5 w-2.5 text-red-500" data-id="element-256" data-mp="2f0k3x01mykt8" />
                    </button>
                  </div>
                  <EmailCanvasBlock block={cb} styles={styles} data-id="element-257" data-mp="32q4o1m9izo43" />
                </div>)}
          </div>)}
      </div>
    </div>;
}
// ── Canvas Preview ──
function EmailCanvasBlock({
  block,
  styles
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 16,
    paddingBottom: block.paddingBottom ?? 16,
    paddingLeft: block.paddingLeft ?? 16,
    paddingRight: block.paddingRight ?? 16
  };
  const bg = block.backgroundColor || 'transparent';
  const getImageStyle = (isImageText: boolean): React.CSSProperties => {
    const style: React.CSSProperties = {
      display: 'block'
    };
    const widthProp = isImageText ? 'imageTextImageWidth' : 'imageWidth';
    const sizing = block.imageSizing || 'maxWidth';
    if (sizing === 'natural') {
      style.maxWidth = 'none';
    } else if (sizing === 'custom') {
      if (block.imageHeight) style.height = `${block.imageHeight}px`;else style.height = 'auto';
      if (block.imageWidth) style.width = `${block.imageWidth}px`;
      if (block.imageMaxWidth) style.maxWidth = `${block.imageMaxWidth}%`;else style.maxWidth = '100%';
    } else {
      const w = (block as any)[widthProp] || block.imageMaxWidth || (isImageText ? 100 : 100);
      style.width = `${w}%`;
      style.maxWidth = '100%';
      style.height = 'auto';
    }
    if (block.imageMaxHeight) style.maxHeight = `${block.imageMaxHeight}px`;
    if (block.imageObjectFit) style.objectFit = block.imageObjectFit as any;
    if (block.imageBorderRadius) style.borderRadius = `${block.imageBorderRadius}px`;
    if (block.imageOpacity != null && block.imageOpacity < 100) style.opacity = block.imageOpacity / 100;
    if (block.imageShadow === 'sm') style.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';else if (block.imageShadow === 'md') style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';else if (block.imageShadow === 'lg') style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
    const align = block.imageAlign || block.align || 'center';
    if (align === 'center') style.margin = '0 auto';else if (align === 'right') style.marginLeft = 'auto';else if (align === 'left') style.marginRight = 'auto';
    return style;
  };
  switch (block.type) {
    case 'text':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 16,
        lineHeight: block.lineHeight || 1.6,
        color: block.color || styles.textColor,
        textAlign: block.align || 'left'
      }} data-id="element-258" data-mp="37f5qgrip8hp5">
          {block.content || 'Add text...'}
        </div>;
    case 'heading':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 28,
        fontWeight: block.fontWeight || 'bold',
        lineHeight: block.lineHeight || 1.3,
        color: block.color || '#111827',
        textAlign: block.align || 'center'
      }} data-id="element-259" data-mp="2opppy470kp8q">
          {block.content || 'Heading'}
        </div>;
    case 'button':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-260" data-mp="7ftdem8eq37t">
          <div style={{
          display: block.buttonWidth === 'full' ? 'block' : 'inline-block',
          backgroundColor: block.buttonBgColor || '#3b82f6',
          color: block.buttonTextColor || '#ffffff',
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 16,
          fontWeight: block.fontWeight || '600',
          padding: '12px 24px',
          borderRadius: block.buttonBorderRadius || 4,
          textAlign: 'center'
        }} data-id="element-261" data-mp="3pnit6fgk9auo">
            {block.buttonText || 'Click Here'}
          </div>
        </div>;
    case 'image':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.imageAlign || block.align || 'center'
      }} data-id="element-262" data-mp="31uai91um1z8s">
          {block.src ? <img src={block.src} alt={block.alt} style={getImageStyle(false)} data-id="element-263" data-mp="2dc0k9bui13xe" /> : <div style={{
          background: '#f3f4f6',
          border: '2px dashed #d1d5db',
          padding: 40,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 14
        }} data-id="element-264" data-mp="2rb9qpjswkjo6">
              Image — set URL in properties
            </div>}
        </div>;
    case 'video_thumbnail':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-265" data-mp="3b0k6sywk96mt">
          <div style={{
          position: 'relative',
          display: 'inline-block'
        }} data-id="element-266" data-mp="3jydy1vfgxa34">
            {block.src ? <img src={block.src} alt={block.alt} style={{
            maxWidth: '100%',
            borderRadius: 8
          }} data-id="element-267" data-mp="l6uxv8k5hpps" /> : <div style={{
            background: '#1f2937',
            padding: '60px 80px',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14
          }} data-id="element-268" data-mp="b7no0vcmkd7f">
                Video Thumbnail
              </div>}
            <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 50,
            height: 50,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-269" data-mp="3tl3owc70y8fu">
              <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-270" data-mp="1q6jgd056g5eu" />
            </div>
          </div>
        </div>;
    case 'divider':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-271" data-mp="1u97rxfl9zqfi">
          <hr style={{
          border: 'none',
          borderTop: `${block.dividerThickness || 1}px ${block.dividerStyle || 'solid'} ${block.dividerColor || '#e5e7eb'}`
        }} data-id="element-272" data-mp="245j66b4feqcl" />
        </div>;
    case 'spacer':
      return <div style={{
        height: block.height || 20,
        backgroundColor: bg
      }} className="flex items-center justify-center text-xs text-gray-400" data-id="element-273" data-mp="1bh56lqxovlyx">
          ↕ {block.height || 20}px
        </div>;
    case 'social':
      {
        const socialColors: Record<string, string> = {
          facebook: '#1877F2',
          twitter: '#000000',
          instagram: '#E1306C',
          linkedin: '#0A66C2',
          youtube: '#FF0000',
          tiktok: '#000000',
          pinterest: '#BD081C',
          whatsapp: '#25D366'
        };
        const socialSvgs: Record<string, string> = {
          facebook: '<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>',
          twitter: '<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>',
          instagram: '<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>',
          linkedin: '<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>',
          youtube: '<path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>',
          tiktok: '<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>',
          pinterest: '<path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>',
          whatsapp: '<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>'
        };
        return <div style={{
          ...pad,
          backgroundColor: bg,
          textAlign: block.align || 'center'
        }} data-id="element-274" data-mp="88f5tsj8qu01">
          <div style={{
            display: 'inline-flex',
            gap: 8
          }} data-id="element-275" data-mp="3qkbtjrna6uaw">
            {(block.socialLinks || []).map((link, i) => {
              const color = socialColors[link.platform.toLowerCase()] || '#6b7280';
              const svgPath = socialSvgs[link.platform.toLowerCase()] || '';
              const size = block.socialIconSize || 32;
              return <div key={i} style={{
                width: size,
                height: size,
                backgroundColor: color,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} title={link.platform} data-id="element-276" data-mp="3bo4349la4wn6" data-mpi={i}>
                  <svg viewBox="0 0 24 24" fill="#ffffff" style={{
                  width: size * 0.55,
                  height: size * 0.55
                }} data-id="element-277" data-mp="3ouyu952htxeg">
                    <g dangerouslySetInnerHTML={{
                    __html: svgPath
                  }} data-id="element-278" data-mp="3b3ldfyvyzdn4" />
                  </svg>
                </div>;
            })}
          </div>
        </div>;
      }
    case 'unsubscribe':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align as any || 'center',
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.5,
        color: block.color || '#9ca3af'
      }} dangerouslySetInnerHTML={{
        __html: block.content || '<a href="#" style="color:#6b7280;">Unsubscribe</a>'
      }} data-id="element-279" data-mp="2w6kxstr1z950" />;
    case 'image_text':
      const imgWidth = block.imageTextImageWidth || 50;
      const textWidth = 100 - imgWidth;
      return <div style={{
        ...pad,
        backgroundColor: bg,
        display: 'flex',
        flexDirection: block.imageTextLayout === 'image-right' ? 'row-reverse' : 'row',
        gap: 20
      }} data-id="element-280" data-mp="1glsgk7l5qy1t">
          <div style={{
          width: `${imgWidth}%`
        }} data-id="element-281" data-mp="sxiv56ruwcb7">
            {block.imageTextImageSrc ? <img src={block.imageTextImageSrc} alt={block.imageTextImageAlt || ''} style={getImageStyle(true)} data-id="element-282" data-mp="xx5dfumjkiqv" /> : <div style={{
            background: '#f3f4f6',
            border: '2px dashed #d1d5db',
            padding: 20,
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 14
          }} data-id="element-283" data-mp="29a7pccdp4k4p">
                Image
              </div>}
          </div>
          <div style={{
          width: `${textWidth}%`
        }} data-id="element-284" data-mp="3q45i0zl9jps">
            <div style={{
            fontFamily: styles.fontFamily,
            fontSize: block.fontSize || 16,
            lineHeight: block.lineHeight || 1.6,
            color: block.color || styles.textColor,
            textAlign: block.align || 'left',
            whiteSpace: 'pre-wrap'
          }} data-id="element-285" data-mp="3289stubypvqs">
              {block.imageTextContent || 'Add text here...'}
            </div>
          </div>
        </div>;
    case 'footer':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 12,
        lineHeight: block.lineHeight || 1.5,
        color: block.color || '#9ca3af',
        textAlign: block.align || 'center'
      }} data-id="element-286" data-mp="vfpg567uqoo2">
          {block.footerLinks && block.footerLinks.length > 0 && <div style={{
          marginBottom: 10
        }} data-id="element-287" data-mp="226v72hsb6opg">
              {block.footerLinks.map((l, i) => <a key={i} href={l.url} style={{
            color: styles.linkColor,
            textDecoration: 'underline',
            margin: '0 10px'
          }} data-id="element-288" data-mp="1s9qfu3jlw31s" data-mpi={i}>
                  {l.label}
                </a>)}
            </div>}
          <div style={{
          marginBottom: 5,
          fontWeight: 'bold'
        }} data-id="element-289" data-mp="3ijtr9az713on">
            {block.footerCompanyName || 'Company Name'}
          </div>
          <div data-id="element-290" data-mp="jxuenyx4vn7">{block.footerAddress || 'Address'}</div>
        </div>;
    case 'table':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-291" data-mp="5eln6z0ygc7l">
          <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 14,
          color: block.color || styles.textColor,
          textAlign: block.align || 'left'
        }} border={block.tableData ? 0 : 1} borderColor={block.tableData ? undefined : '#e5e7eb'} cellPadding={block.tableData ? 0 : 10} data-id="element-292" data-mp="3spupkvlpqd9y">
            <tbody dangerouslySetInnerHTML={{
            __html: block.content || ''
          }} data-id="element-293" data-mp="fcrnjc745aac" />
          </table>
        </div>;
    case 'boxed_text':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-294" data-mp="2shmyg6czxeg4">
          <div style={{
          border: block.boxedTextBorderColor ? `1px solid ${block.boxedTextBorderColor}` : 'none',
          borderRadius: block.boxedTextBorderRadius || 0,
          backgroundColor: block.boxedTextBgColor || 'transparent',
          padding: 20,
          fontFamily: styles.fontFamily,
          fontSize: block.fontSize || 16,
          lineHeight: block.lineHeight || 1.6,
          color: block.color || styles.textColor,
          textAlign: block.align || 'left'
        }} data-id="element-295" data-mp="2nitpmr8nmbma">
            {block.content || 'Boxed text content'}
          </div>
        </div>;
    case 'html_block':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-296" data-mp="1q1v2vodinse2">
          <div style={{
          padding: 10,
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          color: '#64748b',
          fontSize: 12,
          fontFamily: 'monospace',
          overflow: 'hidden'
        }} data-id="element-297" data-mp="1l3jt1dn81hvu">
            HTML Block Preview
            <br data-id="element-298" data-mp="28n50oauh0rc6" />
            {block.htmlContent ? <span className="text-gray-400 truncate block" data-id="element-299" data-mp="2dghqupest671">
                {block.htmlContent.substring(0, 50)}...
              </span> : 'Empty HTML'}
          </div>
        </div>;
    default:
      return <div style={pad} data-id="element-300" data-mp="2rfhd5qeu9901">Unknown block</div>;
  }
}
// ── Properties Panels ──
function EmailBlockProperties({
  block,
  isChild,
  onChange,
  onBlur,
  onDelete
}: {
  block: EmailBlock;
  isChild: boolean;
  onChange: (u: Partial<EmailBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-301" data-mp="1209ho54v2wme">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-302" data-mp="121c86by9ux1s">
        <div data-id="element-303" data-mp="3fbugubmyeduf">
          <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-304" data-mp="3fbm0uavjasf0">
            {block.type.replace('_', ' ')}
          </h3>
          {isChild && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-medium" data-id="element-305" data-mp="36i90woz1yqjf">
              Inside column
            </span>}
        </div>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" data-id="element-306" data-mp="31hl0xdsuizd2">
          <Trash2Icon className="h-4 w-4" data-id="element-307" data-mp="3u8ct806sj3v4" />
        </button>
      </div>

      {(block.type === 'text' || block.type === 'heading' || block.type === 'unsubscribe' || block.type === 'boxed_text') && <>
          <EProp label="Content" data-id="element-308" data-mp="3k9435if1q9e1">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={4} className="prop-input text-sm" data-id="element-309" data-mp="3kc00p5mzew1y" />
          </EProp>
          <EProp label="Font Size" data-id="element-310" data-mp="2faqotrrdn3gm">
            <input type="number" value={block.fontSize || 16} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} min={10} max={72} className="prop-input" data-id="element-311" data-mp="2f9pfbrq0ep10" />
          </EProp>
          <EProp label="Color" data-id="element-312" data-mp="1wcedq0gwnjie">
            <div className="flex gap-2" data-id="element-313" data-mp="1wdplschdvdfb">
              <input type="color" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-314" data-mp="1ikzvhvutnmce" />
              <input type="text" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-315" data-mp="1dlwqgbeudg4f" />
            </div>
          </EProp>
          <EProp label="Align" data-id="element-316" data-mp="yyesrwzp5lri">
            <select value={block.align || 'left'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-317" data-mp="yxd58t1hv6sw">
              <option value="left" data-id="element-318" data-mp="1mnh991ilh8kf">Left</option>
              <option value="center" data-id="element-319" data-mp="1mqd5mlesnp6r">Center</option>
              <option value="right" data-id="element-320" data-mp="1hqh54w5b2fvy">Right</option>
            </select>
          </EProp>
          {block.type === 'heading' && <EProp label="Font Weight" data-id="element-321" data-mp="1vpgu64wqcwsn">
              <select value={block.fontWeight || 'bold'} onChange={e => {
          onChange({
            fontWeight: e.target.value
          });
          onBlur();
        }} className="prop-input" data-id="element-322" data-mp="1ro5aeg0jf5sd">
                <option value="normal" data-id="element-323" data-mp="380hfq9atng0h">Normal</option>
                <option value="500" data-id="element-324" data-mp="37yms2n8619sp">Medium</option>
                <option value="600" data-id="element-325" data-mp="3c2m874pja6lj">Semibold</option>
                <option value="bold" data-id="element-326" data-mp="3c000n52n7r3b">Bold</option>
              </select>
            </EProp>}
          <EProp label="Line Height" data-id="element-327" data-mp="2fa8vmbwq6lj9">
            <input type="number" value={block.lineHeight || 1.6} onChange={e => onChange({
          lineHeight: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={3} step={0.1} className="prop-input" data-id="element-328" data-mp="2kade27h71qc4" />
          </EProp>
        </>}

      {block.type === 'button' && <>
          <EProp label="Button Text" data-id="element-329" data-mp="1t3eiqnnfmllr">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-330" data-mp="1t2d6wgyldv2z" />
          </EProp>
          <EProp label="Link URL" data-id="element-331" data-mp="1f3ls0sbwo5ds">
            <input type="url" value={block.buttonUrl || ''} onChange={e => onChange({
          buttonUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-332" data-mp="1a4ir9k2yklbm" />
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-333" data-mp="weytkso8ie96">
              <button onClick={() => {
            onChange({
              buttonUrl: '{{confirmation_link}}'
            });
            onBlur();
          }} className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" title="Insert confirmation link" data-id="element-334" data-mp="reuacuvxz5f9">
                <TagIcon className="h-3 w-3" data-id="element-335" data-mp="5at6iux3ws6j" />
                <span data-id="element-336" data-mp="bzvd95ald9j">&#123;&#123;confirmation_link&#125;&#125;</span>
              </button>
              <button onClick={() => {
            onChange({
              buttonUrl: '{{unsubscribe_link}}'
            });
            onBlur();
          }} className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors" title="Insert unsubscribe link" data-id="element-337" data-mp="38s1kv4sml9wi">
                <TagIcon className="h-3 w-3" data-id="element-338" data-mp="2pzz3fr85tpl7" />
                <span data-id="element-339" data-mp="2l30d7jrma2sa">&#123;&#123;unsubscribe_link&#125;&#125;</span>
              </button>
            </div>
            <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 p-2 text-[10px] text-blue-700 leading-relaxed" data-id="element-340" data-mp="10k6c4agh1ock">
              <strong data-id="element-341" data-mp="15c6esanxevcu">Note:</strong> The actual confirmation URL is configured
              in the Form Configuration settings where this template is linked.
            </div>
          </EProp>
          <EProp label="Background Color" data-id="element-342" data-mp="vmmsev108jd8">
            <div className="flex gap-2" data-id="element-343" data-mp="vj8qtp060e6u">
              <input type="color" value={block.buttonBgColor || '#3b82f6'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-344" data-mp="1jaxqxrj8a6yc" />
              <input type="text" value={block.buttonBgColor || '#3b82f6'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-345" data-mp="1f7reg25ivjjf" />
            </div>
          </EProp>
          <EProp label="Text Color" data-id="element-346" data-mp="20ml2ufqmzgam">
            <div className="flex gap-2" data-id="element-347" data-mp="1vt9ta6ufwlf0">
              <input type="color" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-348" data-mp="1vs8je5p5u4yv" />
              <input type="text" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-349" data-mp="1cunrsqv0gsak" />
            </div>
          </EProp>
          <EProp label="Border Radius" data-id="element-350" data-mp="p03y9uwpwpsx">
            <input type="number" value={block.buttonBorderRadius || 4} onChange={e => onChange({
          buttonBorderRadius: Number(e.target.value)
        })} onBlur={onBlur} min={0} max={50} className="prop-input" data-id="element-351" data-mp="oz2lnm01zv93" />
          </EProp>
          <EProp label="Width" data-id="element-352" data-mp="62aukqe9sbuh">
            <select value={block.buttonWidth || 'auto'} onChange={e => {
          onChange({
            buttonWidth: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-353" data-mp="632s46rfgjne">
              <option value="auto" data-id="element-354" data-mp="jy4u4it5loux">Auto (fit text)</option>
              <option value="full" data-id="element-355" data-mp="1qfrwulx8wiio">Full Width</option>
            </select>
          </EProp>
        </>}

      {block.type === 'image' && <EmailImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-356" data-mp="1tyhbfi8nhpor" />}

      {block.type === 'video_thumbnail' && <>
          <EProp label="Video URL" data-id="element-357" data-mp="1p1qy32o8d871">
            <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
          videoUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="YouTube or Vimeo URL" data-id="element-358" data-mp="22zfh04yqm59g" />
          </EProp>
          <EProp label="Thumbnail URL" data-id="element-359" data-mp="2mn6ehmt9o535">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Auto-detected from YouTube" data-id="element-360" data-mp="2mm4sinappy6n" />
          </EProp>
          <EProp label="Alt Text" data-id="element-361" data-mp="3aat91hfzbffz">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-362" data-mp="3abuvei2g3od0" />
          </EProp>
        </>}

      {block.type === 'divider' && <>
          <EProp label="Color" data-id="element-363" data-mp="2rjs8wq34hduu">
            <div className="flex gap-2" data-id="element-364" data-mp="2rih4qp5rm437">
              <input type="color" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-365" data-mp="2waig88v3zqs6" />
              <input type="text" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-366" data-mp="28mir1ddg48fp" />
            </div>
          </EProp>
          <EProp label="Thickness" data-id="element-367" data-mp="9f2cy905bebx">
            <input type="number" value={block.dividerThickness || 1} onChange={e => onChange({
          dividerThickness: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={10} className="prop-input" data-id="element-368" data-mp="9bwkqkw7zwbz" />
          </EProp>
          <EProp label="Style" data-id="element-369" data-mp="t4cp5d0onh5a">
            <select value={block.dividerStyle || 'solid'} onChange={e => {
          onChange({
            dividerStyle: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-370" data-mp="o3op61uh7pyx">
              <option value="solid" data-id="element-371" data-mp="1obvg1vzddhfo">Solid</option>
              <option value="dashed" data-id="element-372" data-mp="1od6kly0n2t7u">Dashed</option>
              <option value="dotted" data-id="element-373" data-mp="1t9wzwj4mdks7">Dotted</option>
            </select>
          </EProp>
        </>}

      {block.type === 'spacer' && <EProp label="Height (px)" data-id="element-374" data-mp="1xaqm982eyriz">
          <input type="number" value={block.height || 20} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-375" data-mp="222i3grzojkwg" />
        </EProp>}

      {block.type === 'columns' && <EProp label="Column Ratio" data-id="element-376" data-mp="2l1m8lpo3ouiu">
          <select value={block.columnRatio || '50-50'} onChange={e => {
        const ratio = e.target.value as any;
        const colCount = ratio === '33-33-33' ? 3 : 2;
        const cols = [...(block.columns || [[], []])];
        while (cols.length < colCount) cols.push([]);
        onChange({
          columnRatio: ratio,
          columns: cols.slice(0, colCount)
        });
        onBlur();
      }} className="prop-input" data-id="element-377" data-mp="2l0l09sygym4t">
            <option value="50-50" data-id="element-378" data-mp="glr2medp7byr">50 / 50</option>
            <option value="33-67" data-id="element-379" data-mp="cht5g97xob8c">33 / 67</option>
            <option value="67-33" data-id="element-380" data-mp="ck5tn44abhtu">67 / 33</option>
            <option value="33-33-33" data-id="element-381" data-mp="3kx36de037aej">33 / 33 / 33</option>
          </select>
        </EProp>}

      {block.type === 'image_text' && <EmailImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-382" data-mp="3g0utk79d3taj" />}

      {block.type === 'footer' && <>
          <EProp label="Company Name" data-id="element-383" data-mp="3oxwt441smj5u">
            <input type="text" value={block.footerCompanyName || ''} onChange={e => onChange({
          footerCompanyName: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-384" data-mp="198budfy35q2u" />
          </EProp>
          <EProp label="Address" data-id="element-385" data-mp="1gswbfk35rgoa">
            <textarea value={block.footerAddress || ''} onChange={e => onChange({
          footerAddress: e.target.value
        })} onBlur={onBlur} rows={2} className="prop-input text-sm" data-id="element-386" data-mp="1gpqjzy7242pe" />
          </EProp>
          <div className="space-y-3" data-id="element-387" data-mp="20gc0d34ybfae">
            <div className="flex items-center justify-between" data-id="element-388" data-mp="20farn5ber4vu">
              <span className="text-xs font-medium text-gray-700" data-id="element-389" data-mp="1lrpo5aamf3ne">Links</span>
              <button onClick={() => {
            const links = [...(block.footerLinks || [])];
            links.push({
              label: 'New Link',
              url: '#'
            });
            onChange({
              footerLinks: links
            });
            onBlur();
          }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-390" data-mp="1qpr59pohksw8">
                <PlusIcon className="h-3 w-3" data-id="element-391" data-mp="9pegh2pteewr" /> Add
              </button>
            </div>
            {(block.footerLinks || []).map((link, i) => <div key={i} className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-392" data-mp="drsyjklntamz" data-mpi={i}>
                <div className="flex items-center gap-1" data-id="element-393" data-mp="16fosihjugog2">
                  <input type="text" value={link.label} onChange={e => {
              const links = [...(block.footerLinks || [])];
              links[i] = {
                ...links[i],
                label: e.target.value
              };
              onChange({
                footerLinks: links
              });
            }} onBlur={onBlur} placeholder="Label" className="prop-input flex-1 text-xs" data-id="element-394" data-mp="16f59exigju0g" />
                  <button onClick={() => {
              const links = (block.footerLinks || []).filter((_, j) => j !== i);
              onChange({
                footerLinks: links
              });
              onBlur();
            }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-395" data-mp="35ooofgyg81nq">
                    <XIcon className="h-3 w-3" data-id="element-396" data-mp="30nsm37tqwnkf" />
                  </button>
                </div>
                <input type="url" value={link.url} onChange={e => {
            const links = [...(block.footerLinks || [])];
            links[i] = {
              ...links[i],
              url: e.target.value
            };
            onChange({
              footerLinks: links
            });
          }} onBlur={onBlur} placeholder="https://..." className="prop-input text-xs w-full" data-id="element-397" data-mp="2vwiysdvo5wbp" />
              </div>)}
          </div>
          <EProp label="Font Size" data-id="element-398" data-mp="kvlukdued5r6">
            <input type="number" value={block.fontSize || 12} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-399" data-mp="3kbik2kev96a0" />
          </EProp>
          <EProp label="Text Color" data-id="element-400" data-mp="26yq9xk1oken">
            <div className="flex gap-2" data-id="element-401" data-mp="23t0sh7i7gie">
              <input type="color" value={block.color || '#9ca3af'} onChange={e => onChange({
            color: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-402" data-mp="18mhjn37gxmuq" />
              <input type="text" value={block.color || '#9ca3af'} onChange={e => onChange({
            color: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-403" data-mp="1ir7obgu9b52n" />
            </div>
          </EProp>
        </>}

      {block.type === 'table' && <EmailTableBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-404" data-mp="101rl2lr6syij" />}

      {block.type === 'boxed_text' && <>
          <EProp label="Box Background" data-id="element-405" data-mp="r0thg4knut0h">
            <div className="flex gap-2" data-id="element-406" data-mp="2pev2wxdgqxip">
              <input type="color" value={block.boxedTextBgColor || '#f9fafb'} onChange={e => onChange({
            boxedTextBgColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-407" data-mp="2pdti416nawns" />
              <input type="text" value={block.boxedTextBgColor || '#f9fafb'} onChange={e => onChange({
            boxedTextBgColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-408" data-mp="2jfjj3im801uw" />
            </div>
          </EProp>
          <EProp label="Border Color" data-id="element-409" data-mp="1zrskfxfyfvzm">
            <div className="flex gap-2" data-id="element-410" data-mp="1zsvak35gdueh">
              <input type="color" value={block.boxedTextBorderColor || '#e5e7eb'} onChange={e => onChange({
            boxedTextBorderColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-411" data-mp="24q3wmetq2il0" />
              <input type="text" value={block.boxedTextBorderColor || '#e5e7eb'} onChange={e => onChange({
            boxedTextBorderColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-412" data-mp="1c4f83481pohx" />
            </div>
          </EProp>
          <EProp label="Border Radius" data-id="element-413" data-mp="1v0ok7lze04za">
            <input type="number" value={block.boxedTextBorderRadius || 0} onChange={e => onChange({
          boxedTextBorderRadius: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-414" data-mp="1q46hernmagpl" />
          </EProp>
        </>}

      {block.type === 'html_block' && <EProp label="Custom HTML" data-id="element-415" data-mp="28v7jvpny4udw">
          <textarea value={block.htmlContent || ''} onChange={e => onChange({
        htmlContent: e.target.value
      })} onBlur={onBlur} rows={10} className="prop-input text-sm font-mono" placeholder="<div>...</div>" data-id="element-416" data-mp="28s3743wf0qbm" />
        </EProp>}

      {/* Social Links Editor */}
      {block.type === 'social' && <div className="space-y-3" data-id="element-417" data-mp="1p813d0la2e85">
          <div className="flex items-center justify-between" data-id="element-418" data-mp="1p6zqqrom5job">
            <span className="text-xs font-medium text-gray-700" data-id="element-419" data-mp="1u5jcos37ivgo">
              Social Links
            </span>
            <button onClick={() => {
          const links = [...(block.socialLinks || [])];
          // Find a platform not yet used
          const used = new Set(links.map(l => l.platform));
          const next = SOCIAL_PLATFORMS.find(p => !used.has(p)) || 'facebook';
          links.push({
            platform: next,
            url: '#'
          });
          onChange({
            socialLinks: links
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-420" data-mp="1z6gvn6w1jtj8">
              <PlusIcon className="h-3 w-3" data-id="element-421" data-mp="3jnzskf9twy7b" /> Add
            </button>
          </div>
          {(block.socialLinks || []).map((link, i) => <div key={i} className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-422" data-mp="k7t4hwq91i76" data-mpi={i}>
              <div className="flex items-center gap-1" data-id="element-423" data-mp="b7mrsd2dkm92">
                <select value={link.platform} onChange={e => {
            const links = [...(block.socialLinks || [])];
            links[i] = {
              ...links[i],
              platform: e.target.value
            };
            onChange({
              socialLinks: links
            });
          }} className="prop-input flex-1 text-xs capitalize" data-id="element-424" data-mp="b63d2qwdaj9e">
                  {SOCIAL_PLATFORMS.map(p => <option key={p} value={p} data-id="element-425" data-mp="kxzvsccdwudi">
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </option>)}
                </select>
                <button onClick={() => {
            const links = (block.socialLinks || []).filter((_, j) => j !== i);
            onChange({
              socialLinks: links
            });
            onBlur();
          }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-426" data-mp="1dm5lzem7jbkq">
                  <XIcon className="h-3 w-3" data-id="element-427" data-mp="1wkq6gntlfipe" />
                </button>
              </div>
              <input type="url" value={link.url} onChange={e => {
          const links = [...(block.socialLinks || [])];
          links[i] = {
            ...links[i],
            url: e.target.value
          };
          onChange({
            socialLinks: links
          });
        }} onBlur={onBlur} placeholder="https://..." className="prop-input text-xs w-full" data-id="element-428" data-mp="21c88zgb9lrch" />
            </div>)}
          <EProp label="Icon Size" data-id="element-429" data-mp="2y46d7e33kke3">
            <input type="number" value={block.socialIconSize || 32} onChange={e => onChange({
          socialIconSize: Number(e.target.value)
        })} onBlur={onBlur} min={16} max={64} className="prop-input" data-id="element-430" data-mp="2y57xm9606j8h" />
          </EProp>
          <EProp label="Align" data-id="element-431" data-mp="mff53r47vk7o">
            <select value={block.align || 'center'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-432" data-mp="medqxdrwmhlq">
              <option value="left" data-id="element-433" data-mp="s11zdre1a38q">Left</option>
              <option value="center" data-id="element-434" data-mp="n1p1mblmjbrd">Center</option>
              <option value="right" data-id="element-435" data-mp="n304m978wfhf">Right</option>
            </select>
          </EProp>
        </div>}

      {/* Padding — shared for all blocks */}
      <div className="pt-3 border-t border-gray-200" data-id="element-436" data-mp="1fqcp8dtuid83">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-437" data-mp="1fqw9w2avrfpt">
          Padding
        </span>
        <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-438" data-mp="1ax1jyh4om4ia">
          <EProp label="Top" data-id="element-439" data-mp="15zi5bg42nppt">
            <input type="number" value={block.paddingTop ?? 10} onChange={e => onChange({
            paddingTop: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-440" data-mp="15y72pjmd4o0a" />
          </EProp>
          <EProp label="Bottom" data-id="element-441" data-mp="2b09cra2v1xs5">
            <input type="number" value={block.paddingBottom ?? 10} onChange={e => onChange({
            paddingBottom: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-442" data-mp="2azgdeu68eak1" />
          </EProp>
          <EProp label="Left" data-id="element-443" data-mp="2tx0rr7bddqqe">
            <input type="number" value={block.paddingLeft ?? 20} onChange={e => onChange({
            paddingLeft: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-444" data-mp="2tw99ey6s9pi2" />
          </EProp>
          <EProp label="Right" data-id="element-445" data-mp="3cns5sm6ay5b4">
            <input type="number" value={block.paddingRight ?? 20} onChange={e => onChange({
            paddingRight: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-446" data-mp="3cotlj3y9jfz6" />
          </EProp>
        </div>
      </div>

      <EProp label="Background" data-id="element-447" data-mp="1hju8dh11qre3">
        <div className="flex gap-2" data-id="element-448" data-mp="1hjkfnloma64p">
          <input type="color" value={block.backgroundColor || '#ffffff'} onChange={e => onChange({
          backgroundColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-449" data-mp="1w9i8wg1elm0r" />
          <input type="text" value={block.backgroundColor || ''} onChange={e => onChange({
          backgroundColor: e.target.value
        })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" placeholder="transparent" data-id="element-450" data-mp="21afr2smeyg29" />
        </div>
      </EProp>
    </div>;
}
function EmailStyleProperties({
  styles,
  onChange
}: {
  styles: EmailGlobalStyles;
  onChange: (s: EmailGlobalStyles) => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-451" data-mp="3j56oxgsh4tgd">
      <div className="pb-3 border-b border-gray-200" data-id="element-452" data-mp="3o5kpkdrqx14d">
        <div className="flex items-center gap-2" data-id="element-453" data-mp="3o3zpy9qkqrzn">
          <PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-454" data-mp="398k2wooevwcr" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-455" data-mp="39b6b8qj4mfw1">
            Design Settings
          </h3>
        </div>
      </div>

      <div className="space-y-4" data-id="element-456" data-mp="308orqjuuw1cq">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-457" data-mp="309q20m41sjte">
          Layout
        </h4>
        <EProp label="Template Width (px)" data-id="element-458" data-mp="359cdaq0bfpzk">
          <input type="number" value={styles.contentWidth} onChange={e => onChange({
          ...styles,
          contentWidth: Number(e.target.value)
        })} min={400} max={800} className="prop-input" data-id="element-459" data-mp="yg8y9tj1g33x" />
        </EProp>
        <EProp label="Enable RTL layout" data-id="element-460" data-mp="2uhu4d45mxi5b">
          <div className="flex items-center mt-1" data-id="element-461" data-mp="2pkaoy0x7azbs">
            <input type="checkbox" checked={styles.rtlEnabled || false} onChange={e => onChange({
            ...styles,
            rtlEnabled: e.target.checked
          })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-462" data-mp="2pmwyg63njown" />
            <span className="ml-2 text-sm text-gray-600" data-id="element-463" data-mp="26v468dweq7pz">
              Right-to-left text
            </span>
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-464" data-mp="1xteef6ym56ro">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-465" data-mp="1xsuszg9r808w">
          Colors
        </h4>
        <EProp label="Inner Color (Content)" data-id="element-466" data-mp="1swxezglyr5za">
          <div className="flex gap-2" data-id="element-467" data-mp="8bh6q8ruae1v">
            <input type="color" value={styles.innerColor || '#ffffff'} onChange={e => onChange({
            ...styles,
            innerColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-468" data-mp="8emwzrc7flz6" />
            <input type="text" value={styles.innerColor || '#ffffff'} onChange={e => onChange({
            ...styles,
            innerColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-469" data-mp="3lrylnucqb8n6" />
          </div>
        </EProp>
        <EProp label="Outer Color (Background)" data-id="element-470" data-mp="323fu56493j5b">
          <div className="flex gap-2" data-id="element-471" data-mp="322o8oo4db1sr">
            <input type="color" value={styles.backgroundColor} onChange={e => onChange({
            ...styles,
            backgroundColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-472" data-mp="321likievd3dw" />
            <input type="text" value={styles.backgroundColor} onChange={e => onChange({
            ...styles,
            backgroundColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-473" data-mp="n0m309lciouk" />
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-474" data-mp="49mgdf1bpr4g">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-475" data-mp="47ryk9lcmf4l">
          Border
        </h4>
        <EProp label="Outer Border Size (px)" data-id="element-476" data-mp="1fp1ewn96kr1o">
          <input type="number" value={styles.outerBorderSize || 0} onChange={e => onChange({
          ...styles,
          outerBorderSize: Number(e.target.value)
        })} min={0} max={20} className="prop-input" data-id="element-477" data-mp="1fnqbwpnk7nbm" />
        </EProp>
        <EProp label="Outer Border Color" data-id="element-478" data-mp="1991afngye7px">
          <div className="flex gap-2" data-id="element-479" data-mp="19a2tof84i0iq">
            <input type="color" value={styles.outerBorderColor || '#000000'} onChange={e => onChange({
            ...styles,
            outerBorderColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-480" data-mp="1e7unxfw8pyq3" />
            <input type="text" value={styles.outerBorderColor || '#000000'} onChange={e => onChange({
            ...styles,
            outerBorderColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-481" data-mp="1s6u9geoghxi1" />
          </div>
        </EProp>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-482" data-mp="pljqw77lt2dn">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-483" data-mp="ktjlw0cofja7">
          Typography
        </h4>
        <EProp label="Font Family" data-id="element-484" data-mp="fw0974vmnekd">
          <select value={styles.fontFamily} onChange={e => onChange({
          ...styles,
          fontFamily: e.target.value
        })} className="prop-input" data-id="element-485" data-mp="fsvwfj43jai3">
            <option value="Arial, Helvetica, sans-serif" data-id="element-486" data-mp="13lm6tnwcplq9">Arial</option>
            <option value="Georgia, serif" data-id="element-487" data-mp="ziphzfg7wtdv">Georgia</option>
            <option value="'Trebuchet MS', sans-serif" data-id="element-488" data-mp="zh4olt5n3ghl">Trebuchet MS</option>
            <option value="Verdana, sans-serif" data-id="element-489" data-mp="uj35574axf5l">Verdana</option>
            <option value="'Times New Roman', serif" data-id="element-490" data-mp="3k8x37hxas9s7">Times New Roman</option>
          </select>
        </EProp>
        <EProp label="Text Color" data-id="element-491" data-mp="3f9kessqs2uni">
          <div className="flex gap-2" data-id="element-492" data-mp="3f8isfs4balqh">
            <input type="color" value={styles.textColor} onChange={e => onChange({
            ...styles,
            textColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-493" data-mp="1us1j1noq7w10" />
            <input type="text" value={styles.textColor} onChange={e => onChange({
            ...styles,
            textColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-494" data-mp="28jpr9fvuu0cp" />
          </div>
        </EProp>
        <EProp label="Link Color" data-id="element-495" data-mp="2new00amc4d9q">
          <div className="flex gap-2" data-id="element-496" data-mp="2rillusbmkgr2">
            <input type="color" value={styles.linkColor} onChange={e => onChange({
            ...styles,
            linkColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-497" data-mp="2rhiwiotyamd9" />
            <input type="text" value={styles.linkColor} onChange={e => onChange({
            ...styles,
            linkColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-498" data-mp="12hg348zgk9au" />
          </div>
        </EProp>
      </div>
    </div>;
}
function EProp({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-499" data-mp="1le7rgwtuxelr">
      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-500" data-mp="1z7pdcoa0n3ey">
        {label}
      </label>
      {children}
    </div>;
}
function EmailImageBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: EmailBlock;
  onChange: (updates: Partial<EmailBlock>) => void;
  onBlur: () => void;
}) {
  const {
    currentWorkspace
  } = useWorkspace();
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaImages, setMediaImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'source' | 'layout' | 'effects'>('source');
  const isImageText = block.type === 'image_text';
  const srcProp = isImageText ? 'imageTextImageSrc' : 'src';
  const altProp = isImageText ? 'imageTextImageAlt' : 'alt';
  const widthProp = isImageText ? 'imageTextImageWidth' : 'imageWidth';
  const loadMedia = async () => {
    if (mediaImages.length > 0) return;
    setIsLoadingMedia(true);
    try {
      const all = await mediaLibraryApi.list();
      const teamFiltered = all.filter(img => img.Team === team || img.Team === currentWorkspace.name);
      setMediaImages(teamFiltered);
    } catch (err) {
      console.warn('Failed to load media library:', err);
    } finally {
      setIsLoadingMedia(false);
    }
  };
  const filteredMedia = mediaImages.filter(img => {
    if (!mediaSearch) return true;
    const q = mediaSearch.toLowerCase();
    return (img.Image_Name || '').toLowerCase().includes(q) || (img.Category || '').toLowerCase().includes(q);
  });
  const tabs = [{
    id: 'source' as const,
    label: 'Source',
    icon: ImageIcon
  }, {
    id: 'layout' as const,
    label: 'Layout',
    icon: SlidersHorizontalIcon
  }, {
    id: 'effects' as const,
    label: 'Effects',
    icon: SparklesIcon
  }];
  return <>
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4" data-id="element-501" data-mp="5lrbmtttcdfc">
        {tabs.map(t => {
        const Icon = t.icon;
        return <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-502" data-mp="1njuvyzcnc45">
              <Icon className="h-3 w-3" data-id="element-503" data-mp="15u7jjm1rx1pn" />
              {t.label}
            </button>;
      })}
      </div>

      {activeTab === 'source' && <div className="space-y-4" data-id="element-504" data-mp="1arzb2eyialtb">
          <EProp label="Image URL" data-id="element-505" data-mp="1flbywmvd2ol0">
            <input type="url" value={(block as any)[srcProp] || ''} onChange={e => onChange({
          [srcProp]: e.target.value
        })} onBlur={onBlur} placeholder="https://..." className="prop-input" data-id="element-506" data-mp="1fmd9yrcdnb2q" />
          </EProp>

          <div data-id="element-507" data-mp="1yjwmw61yhduf">
            <button onClick={() => {
          setShowMediaPicker(!showMediaPicker);
          if (!showMediaPicker) loadMedia();
        }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-508" data-mp="1yid6mfgav2sn">
              <ImagePlusIcon className="h-3.5 w-3.5" data-id="element-509" data-mp="300q8ilyxmlc3" />
              Choose from Media Library
            </button>

            {showMediaPicker && <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-510" data-mp="3ewoaikvjejw8">
                <div className="p-2 border-b border-gray-100" data-id="element-511" data-mp="39y3bgos0u69d">
                  <div className="relative" data-id="element-512" data-mp="2mb4yi1341gg7">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-513" data-mp="2hba7zyn8ysvo" />
                    <input type="text" value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Search images..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-514" data-mp="2vzwnc0cvjkmw" />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-2" data-id="element-515" data-mp="e15icetumdr8">
                  {isLoadingMedia ? <div className="flex items-center justify-center py-6" data-id="element-516" data-mp="iv1dzamn3oj8">
                      <Loader2Icon className="h-5 w-5 text-gray-400 animate-spin" data-id="element-517" data-mp="45lmfsfabf2h" />
                    </div> : filteredMedia.length === 0 ? <div className="text-center py-6" data-id="element-518" data-mp="wthgsqhdsuw3">
                      <ImageIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" data-id="element-519" data-mp="wtr7yhe5x83d" />
                      <p className="text-[10px] text-gray-400" data-id="element-520" data-mp="11qzsgoms9o7s">
                        {mediaSearch ? 'No images match your search' : 'No images in library'}
                      </p>
                    </div> : <div className="grid grid-cols-3 gap-1.5" data-id="element-521" data-mp="38iipeuyxnkmg">
                      {filteredMedia.map(img => <button key={img.ID} onClick={() => {
                onChange({
                  [srcProp]: img.Image_URL,
                  [altProp]: img.Image_Name || img.Description || ''
                });
                onBlur();
                setShowMediaPicker(false);
                setMediaSearch('');
              }} className={`relative group rounded-md overflow-hidden border-2 transition-all aspect-square ${(block as any)[srcProp] === img.Image_URL ? 'border-blue-500 ring-1 ring-blue-500' : 'border-transparent hover:border-blue-300'}`} data-id="element-522" data-mp="3cl5bef8xabbq">
                          <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23f3f4f6" width="40" height="40"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10">?</text></svg>';
                }} data-id="element-523" data-mp="lf6ao390j7d7" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-524" data-mp="322fimok9vcjx">
                            <p className="text-[9px] text-white truncate font-medium" data-id="element-525" data-mp="29c5qh9w426f2">
                              {img.Image_Name}
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>
              </div>}
          </div>

          <EProp label="Alt Text" data-id="element-526" data-mp="3uo78p3kk5rcq">
            <input type="text" value={(block as any)[altProp] || ''} onChange={e => onChange({
          [altProp]: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Describe the image..." data-id="element-527" data-mp="3unfdvuysbxni" />
          </EProp>

          {!isImageText && <EProp label="Link URL" data-id="element-528" data-mp="okn4l9qng1xx">
              <input type="url" value={block.href || ''} onChange={e => onChange({
          href: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Optional link" data-id="element-529" data-mp="13b4dtf9izw5d" />
            </EProp>}

          {(block as any)[srcProp] && <div className="rounded-lg border border-gray-200 overflow-hidden mt-2" data-id="element-530" data-mp="3e478z6tn2wxm">
              <img src={(block as any)[srcProp]} alt={(block as any)[altProp] || ''} className="w-full h-24 object-contain bg-gray-50" onError={e => {
          ;
          (e.target as HTMLImageElement).style.display = 'none';
        }} data-id="element-531" data-mp="395niqu80iyzg" />
            </div>}

          {isImageText && <>
              <div className="h-px bg-gray-200 my-4" data-id="element-532" data-mp="1juikuqxfbeve" />
              <EProp label="Text Content" data-id="element-533" data-mp="15w23m61xq5z0">
                <textarea value={block.imageTextContent || ''} onChange={e => onChange({
            imageTextContent: e.target.value
          })} onBlur={onBlur} rows={4} className="prop-input text-sm" data-id="element-534" data-mp="15vai5o21xomg" />
              </EProp>
              <EProp label="Font Size" data-id="element-535" data-mp="2ragb6xygz3jr">
                <input type="number" value={block.fontSize || 16} onChange={e => onChange({
            fontSize: Number(e.target.value)
          })} onBlur={onBlur} className="prop-input" data-id="element-536" data-mp="2r7annn5ho9q5" />
              </EProp>
              <EProp label="Text Color" data-id="element-537" data-mp="au50w0stkxse">
                <div className="flex gap-2" data-id="element-538" data-mp="flwj9o1tnx7g">
                  <input type="color" value={block.color || '#333333'} onChange={e => onChange({
              color: e.target.value
            })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-539" data-mp="fnppx3cfqhfg" />
                  <input type="text" value={block.color || '#333333'} onChange={e => onChange({
              color: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-540" data-mp="ylisu5n26rcu" />
                </div>
              </EProp>
            </>}
        </div>}

      {activeTab === 'layout' && <div className="space-y-4" data-id="element-541" data-mp="2j3uqnyd4jrb5">
          {isImageText && <EProp label="Layout" data-id="element-542" data-mp="2f1pvgv712mme">
              <select value={block.imageTextLayout || 'image-left'} onChange={e => {
          onChange({
            imageTextLayout: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-543" data-mp="2f2r8h57ltj6r">
                <option value="image-left" data-id="element-544" data-mp="32t52h19r1s3z">Image Left</option>
                <option value="image-right" data-id="element-545" data-mp="2xr609upqx18m">Image Right</option>
              </select>
            </EProp>}

          <EProp label="Alignment" data-id="element-546" data-mp="1rdgeq7oo8p24">
            <div className="grid grid-cols-3 gap-1" data-id="element-547" data-mp="1mcshv5drhdzz">
              {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => {
            onChange({
              imageAlign: align
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageAlign || 'center') === align ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-548" data-mp="1ntog9ned2fpm">
                  {align}
                </button>)}
            </div>
          </EProp>

          <EProp label="Sizing Mode" data-id="element-549" data-mp="v6bql4cwlrs3">
            <select value={block.imageSizing || 'maxWidth'} onChange={e => {
          onChange({
            imageSizing: e.target.value as 'natural' | 'maxWidth' | 'custom'
          });
          onBlur();
        }} className="prop-input" data-id="element-550" data-mp="v37hptohu7v3">
              <option value="natural" data-id="element-551" data-mp="7i5ehu4gdv1r">Natural (no constraints)</option>
              <option value="maxWidth" data-id="element-552" data-mp="2k3yji2bqbui">Max Width % (responsive)</option>
              <option value="custom" data-id="element-553" data-mp="gj3cnvs7n8cf">Custom dimensions</option>
            </select>
          </EProp>

          {(block.imageSizing || 'maxWidth') === 'maxWidth' && <EProp label="Max Width" data-id="element-554" data-mp="cjcps35t1cd6">
              <div className="flex items-center gap-2" data-id="element-555" data-mp="qfzni85l06k5">
                <input type="range" min={10} max={100} value={block.imageMaxWidth || (block as any)[widthProp] || (isImageText ? 50 : 100)} onChange={e => {
            onChange({
              imageMaxWidth: Number(e.target.value),
              [widthProp]: Number(e.target.value)
            });
          }} onMouseUp={onBlur} className="flex-1" data-id="element-556" data-mp="qeersf7j6hkp" />
                <span className="text-xs text-gray-500 w-10 text-right" data-id="element-557" data-mp="33nhbfhh4jifj">
                  {block.imageMaxWidth || (block as any)[widthProp] || (isImageText ? 50 : 100)}
                  %
                </span>
              </div>
            </EProp>}

          {block.imageSizing === 'custom' && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-558" data-mp="2frd44jyz5q7b">
                <EProp label="Width (px)" data-id="element-559" data-mp="2kntqb8mm5uhr">
                  <input type="number" value={block.imageHeight || ''} onChange={e => onChange({
              imageHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-560" data-mp="2kqpmositcb43" />
                </EProp>
                <EProp label="Height (px)" data-id="element-561" data-mp="e3mq64pe9zub">
                  <input type="number" value={block.imageMaxHeight || ''} onChange={e => onChange({
              imageMaxHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-562" data-mp="e4oc547y86qt" />
                </EProp>
              </div>
            </>}

          {block.imageSizing === 'natural' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-563" data-mp="1ksmnznefydz5">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-564" data-mp="1gr159lf875h1">
                <strong data-id="element-565" data-mp="1gox0z2df04g8">Natural:</strong> The image will render at its original
                dimensions with no constraints.
              </p>
            </div>}

          <EProp label="Object Fit" data-id="element-566" data-mp="3f1rr2dppmrj4">
            <select value={block.imageObjectFit || 'contain'} onChange={e => {
          onChange({
            imageObjectFit: e.target.value
          });
          onBlur();
        }} className="prop-input" data-id="element-567" data-mp="318sg8p0vhlhw">
              <option value="contain" data-id="element-568" data-mp="1toowv1ar25l">Contain (fit inside)</option>
              <option value="cover" data-id="element-569" data-mp="1svriko89p04">Cover (fill & crop)</option>
              <option value="fill" data-id="element-570" data-mp="3k031oqntx62e">Fill (stretch)</option>
              <option value="none" data-id="element-571" data-mp="3k1mhkg5kpf3n">None (natural size)</option>
            </select>
          </EProp>

          <EProp label="Border Radius" data-id="element-572" data-mp="3p07i6goqm0sm">
            <div className="flex items-center gap-2" data-id="element-573" data-mp="28j3c42sv95c2">
              <input type="range" min={0} max={50} value={block.imageBorderRadius || 0} onChange={e => onChange({
            imageBorderRadius: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-574" data-mp="28mhevc5fzgk1" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-575" data-mp="2ey0q2vrono8f">
                {block.imageBorderRadius || 0}px
              </span>
            </div>
          </EProp>
        </div>}

      {activeTab === 'effects' && <div className="space-y-4" data-id="element-576" data-mp="1hde90rja0ekf">
          <EProp label="Opacity" data-id="element-577" data-mp="1hefwjvhhatj1">
            <div className="flex items-center gap-2" data-id="element-578" data-mp="1w1ivm6lg9a8h">
              <input type="range" min={10} max={100} value={block.imageOpacity ?? 100} onChange={e => onChange({
            imageOpacity: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-579" data-mp="1w33lvk0qe70j" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-580" data-mp="d4d5gsr6odcp">
                {block.imageOpacity ?? 100}%
              </span>
            </div>
          </EProp>

          <EProp label="Shadow" data-id="element-581" data-mp="n0q84g4pi1jl">
            <div className="grid grid-cols-4 gap-1" data-id="element-582" data-mp="mzexbwcuftiz">
              {(['none', 'sm', 'md', 'lg'] as const).map(s => <button key={s} onClick={() => {
            onChange({
              imageShadow: s
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageShadow || 'none') === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-583" data-mp="rxq4fzbr7pxi">
                  {s === 'none' ? 'Off' : s.toUpperCase()}
                </button>)}
            </div>
          </EProp>
        </div>}
    </>;
}
function EmailTableBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: EmailBlock;
  onChange: (updates: Partial<EmailBlock>) => void;
  onBlur: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'visual' | 'html'>('visual');
  // Initialize tableData if missing
  const td = block.tableData || {
    rows: [['Cell 1', 'Cell 2'], ['Cell 3', 'Cell 4']],
    hasHeader: false,
    headerBgColor: '#f3f4f6',
    headerTextColor: '#111827',
    borderColor: '#e5e7eb',
    stripedRows: false,
    stripedColor: '#f9fafb',
    cellAlign: 'left' as const
  };
  const updateTableData = (updates: Partial<typeof td>) => {
    const newTd = {
      ...td,
      ...updates
    };
    // Generate HTML from the new table data
    let html = '';
    newTd.rows.forEach((row, rIdx) => {
      const isHeader = newTd.hasHeader && rIdx === 0;
      const isStriped = newTd.stripedRows && rIdx % 2 === 1 && !isHeader;
      let rowStyle = '';
      if (isHeader) {
        rowStyle = `background-color:${newTd.headerBgColor};color:${newTd.headerTextColor};font-weight:bold;`;
      } else if (isStriped) {
        rowStyle = `background-color:${newTd.stripedColor};`;
      }
      html += `<tr style="${rowStyle}">`;
      row.forEach(cell => {
        const tag = isHeader ? 'th' : 'td';
        html += `<${tag} style="padding:10px;border:1px solid ${newTd.borderColor};text-align:${newTd.cellAlign};">${cell}</${tag}>`;
      });
      html += `</tr>`;
    });
    onChange({
      tableData: newTd,
      content: html
    });
  };
  const addRow = () => {
    const cols = td.rows[0]?.length || 2;
    const newRow = Array(cols).fill('New Cell');
    updateTableData({
      rows: [...td.rows, newRow]
    });
    onBlur();
  };
  const removeRow = (idx: number) => {
    if (td.rows.length <= 1) return;
    const newRows = [...td.rows];
    newRows.splice(idx, 1);
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const addCol = () => {
    const newRows = td.rows.map(row => [...row, 'New Cell']);
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const removeCol = (idx: number) => {
    if ((td.rows[0]?.length || 0) <= 1) return;
    const newRows = td.rows.map(row => {
      const newRow = [...row];
      newRow.splice(idx, 1);
      return newRow;
    });
    updateTableData({
      rows: newRows
    });
    onBlur();
  };
  const updateCell = (rIdx: number, cIdx: number, val: string) => {
    const newRows = [...td.rows];
    newRows[rIdx] = [...newRows[rIdx]];
    newRows[rIdx][cIdx] = val;
    updateTableData({
      rows: newRows
    });
  };
  return <>
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4" data-id="element-584" data-mp="3vhdi8r4uwdaa">
        <button onClick={() => setActiveTab('visual')} className={`flex-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === 'visual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-585" data-mp="3bq9jx3wekoh">
          Visual Editor
        </button>
        <button onClick={() => setActiveTab('html')} className={`flex-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === 'html' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-586" data-mp="29evl30wpbl3r">
          Raw HTML
        </button>
      </div>

      {activeTab === 'visual' && <div className="space-y-4" data-id="element-587" data-mp="2s4lcde199oqx">
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-2 overflow-x-auto" data-id="element-588" data-mp="2s3k2vdzw1abb">
            <div className="flex gap-2 mb-2" data-id="element-589" data-mp="2o289cxdw84xu">
              <button onClick={addRow} className="px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-700 hover:bg-gray-50" data-id="element-590" data-mp="322rcpr7eymto">
                + Add Row
              </button>
              <button onClick={addCol} className="px-2 py-1 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-700 hover:bg-gray-50" data-id="element-591" data-mp="17pjfjsvytz4v">
                + Add Column
              </button>
            </div>

            <div className="inline-block min-w-full" data-id="element-592" data-mp="3jcd4p746c4m8">
              <table className="w-full border-collapse bg-white shadow-sm" data-id="element-593" data-mp="3je6h731gxj25">
                <tbody data-id="element-594" data-mp="34mnzn0synggo">
                  {td.rows.map((row, rIdx) => <tr key={rIdx} className="group" data-id="element-595" data-mp="39l8rocy26tu1" data-mpi={rIdx}>
                      {row.map((cell, cIdx) => <td key={cIdx} className="border border-gray-200 relative p-0" data-id="element-596" data-mp="2vlhlgydsahql" data-mpi={cIdx}>
                          <input type="text" value={cell} onChange={e => updateCell(rIdx, cIdx, e.target.value)} onBlur={onBlur} className="w-full h-full p-2 text-xs border-none focus:ring-1 focus:ring-blue-500" style={{
                    backgroundColor: td.hasHeader && rIdx === 0 ? td.headerBgColor : td.stripedRows && rIdx % 2 === 1 ? td.stripedColor : 'transparent',
                    color: td.hasHeader && rIdx === 0 ? td.headerTextColor : 'inherit',
                    fontWeight: td.hasHeader && rIdx === 0 ? 'bold' : 'normal',
                    textAlign: td.cellAlign
                  }} data-id="element-597" data-mp="2lol7id5493en" />
                          {rIdx === 0 && td.rows[0].length > 1 && <button onClick={() => removeCol(cIdx)} className="absolute -top-2 -right-2 w-4 h-4 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] hover:bg-red-200 z-10" title="Remove column" data-id="element-598" data-mp="1ear6ovemsoz4">
                              ×
                            </button>}
                        </td>)}
                      <td className="w-6 p-0 border-none bg-transparent" data-id="element-599" data-mp="lhe2eesfr9l0">
                        {td.rows.length > 1 && <button onClick={() => removeRow(rIdx)} className="w-4 h-4 ml-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-[10px] hover:bg-red-200" title="Remove row" data-id="element-600" data-mp="qfp7ydbp6xxf">
                            ×
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-601" data-mp="3qu06460ti0c2">
            <EProp label="Header Row" data-id="element-602" data-mp="9el2v5ibusyk">
              <select value={td.hasHeader ? 'yes' : 'no'} onChange={e => {
            updateTableData({
              hasHeader: e.target.value === 'yes'
            });
            onBlur();
          }} className="prop-input" data-id="element-603" data-mp="9dieb48h92lt">
                <option value="yes" data-id="element-604" data-mp="37xrxcs8lj7c0">Yes</option>
                <option value="no" data-id="element-605" data-mp="3mt7x9dvuxxgb">No</option>
              </select>
            </EProp>

            <EProp label="Striped Rows" data-id="element-606" data-mp="3htbtnfr2o81a">
              <select value={td.stripedRows ? 'yes' : 'no'} onChange={e => {
            updateTableData({
              stripedRows: e.target.value === 'yes'
            });
            onBlur();
          }} className="prop-input" data-id="element-607" data-mp="3hsa9mls2wb7f">
                <option value="yes" data-id="element-608" data-mp="14phtsq27gqd8">Yes</option>
                <option value="no" data-id="element-609" data-mp="19n9r5zlmd4ot">No</option>
              </select>
            </EProp>
          </div>

          {td.hasHeader && <div className="grid grid-cols-2 gap-3" data-id="element-610" data-mp="vojs4ef8ouxt">
              <EProp label="Header BG" data-id="element-611" data-mp="znahu0hdlaaw">
                <div className="flex gap-2" data-id="element-612" data-mp="zq4z5j53ef03">
                  <input type="color" value={td.headerBgColor} onChange={e => updateTableData({
              headerBgColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-613" data-mp="1nd4t4dm1qqt1" />
                  <input type="text" value={td.headerBgColor} onChange={e => updateTableData({
              headerBgColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-614" data-mp="26c8v52f67kb7" />
                </div>
              </EProp>
              <EProp label="Header Text" data-id="element-615" data-mp="1cknotp7n8xsn">
                <div className="flex gap-2" data-id="element-616" data-mp="17o6wsjxbpzaa">
                  <input type="color" value={td.headerTextColor} onChange={e => updateTableData({
              headerTextColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-617" data-mp="17pi1clylfb2g" />
                  <input type="text" value={td.headerTextColor} onChange={e => updateTableData({
              headerTextColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-618" data-mp="1qg9bhpqen5hw" />
                </div>
              </EProp>
            </div>}

          <div className="grid grid-cols-2 gap-3" data-id="element-619" data-mp="30iy3ch9byeer">
            <EProp label="Border Color" data-id="element-620" data-mp="35brblf40c4vl">
              <div className="flex gap-2" data-id="element-621" data-mp="35ctylby7ln68">
                <input type="color" value={td.borderColor} onChange={e => updateTableData({
              borderColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-622" data-mp="3aam3r7nv75sd" />
                <input type="text" value={td.borderColor} onChange={e => updateTableData({
              borderColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-623" data-mp="3o7r72zw71qjx" />
              </div>
            </EProp>

            {td.stripedRows && <EProp label="Stripe Color" data-id="element-624" data-mp="15krabpemiqt5">
                <div className="flex gap-2" data-id="element-625" data-mp="15hvblyuyby3n">
                  <input type="color" value={td.stripedColor} onChange={e => updateTableData({
              stripedColor: e.target.value
            })} onBlur={onBlur} className="h-8 w-8 rounded border cursor-pointer" data-id="element-626" data-mp="1ahzs3ovv0stv" />
                  <input type="text" value={td.stripedColor} onChange={e => updateTableData({
              stripedColor: e.target.value
            })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-627" data-mp="1t899i607wr65" />
                </div>
              </EProp>}
          </div>

          <EProp label="Cell Alignment" data-id="element-628" data-mp="35332u305k0mc">
            <select value={td.cellAlign} onChange={e => {
          updateTableData({
            cellAlign: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-629" data-mp="3521gh2dorrpb">
              <option value="left" data-id="element-630" data-mp="28dlnsxfa527z">Left</option>
              <option value="center" data-id="element-631" data-mp="2m3p477rha1pi">Center</option>
              <option value="right" data-id="element-632" data-mp="2m4idoghtn5bc">Right</option>
            </select>
          </EProp>

          <EProp label="Font Size" data-id="element-633" data-mp="2hbxnrq21dcd3">
            <input type="number" value={block.fontSize || 14} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-634" data-mp="2haw08m3u2xeh" />
          </EProp>
        </div>}

      {activeTab === 'html' && <div className="space-y-4" data-id="element-635" data-mp="3tnuifhrecfvf">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 mb-2" data-id="element-636" data-mp="2azxpkbc58bh">
            <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-637" data-mp="2dcbdla28wiq">
              <strong data-id="element-638" data-mp="79b41l2f90oy">Warning:</strong> Editing the raw HTML directly will
              override the visual editor. If you switch back to the visual
              editor and make changes, your custom HTML will be overwritten.
            </p>
          </div>
          <EProp label="Table HTML Content (<tbody>)" data-id="element-639" data-mp="3fpcy7u0p9li9">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={8} className="prop-input text-sm font-mono" data-id="element-640" data-mp="3frz6xwzbu722" />
          </EProp>
          <EProp label="Font Size" data-id="element-641" data-mp="2khanfossymr7">
            <input type="number" value={block.fontSize || 14} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} className="prop-input" data-id="element-642" data-mp="2kg9dxorfq8bl" />
          </EProp>
        </div>}
    </>;
}